import { useEffect, useState } from 'react';
import { clientFetch } from './api';
import ClientShell from './ClientShell';

const styles = {
  container: {
    padding: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: '0',
  },
  tableHeader: {
    borderBottom: '2px solid #1F2937',
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#9CA3AF',
    backgroundColor: '#111827',
  },
  tableRow: {
    borderBottom: '1px solid #1F2937',
    transition: 'background-color 0.2s',
  },
  tableRowHover: {
    backgroundColor: '#0f172a',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#FFF',
  },
  contractNumber: {
    fontWeight: '600',
    color: '#3B82F6',
  },
  contractTitle: {
    fontWeight: '600',
    color: '#FFF',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  badgeActive: {
    backgroundColor: '#10b981',
    color: '#fff',
  },
  badgeExpiring: {
    backgroundColor: '#f59e0b',
    color: '#1f2937',
  },
  badgeExpired: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  daysRemaining: {
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
  },
  daysRemainingWarning: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  daysRemainingNormal: {
    backgroundColor: '#e0f2fe',
    color: '#0284c7',
  },
  renewalIndicator: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#1F2937',
    color: '#3B82F6',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  value: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  dates: {
    fontSize: '13px',
    color: '#9CA3AF',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  errorText: {
    color: '#EF4444',
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9CA3AF',
  },
};

export default function ClientContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        setLoading(true);
        const data = await clientFetch('/client/portal/contracts');
        setContracts(Array.isArray(data) ? data : data.contracts || []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load contracts');
        console.error('Contracts error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const getStatusBadge = (status, daysRemaining) => {
    if (status === 'expired') {
      return <span style={{ ...styles.badge, ...styles.badgeExpired }}>Expired</span>;
    }
    if (daysRemaining !== undefined && daysRemaining < 30 && daysRemaining > 0) {
      return <span style={{ ...styles.badge, ...styles.badgeExpiring }}>Expiring Soon</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgeActive }}>Active</span>;
  };

  const getDaysRemainingStyle = (daysRemaining) => {
    if (daysRemaining < 30 && daysRemaining > 0) {
      return { ...styles.daysRemaining, ...styles.daysRemainingWarning };
    }
    return { ...styles.daysRemaining, ...styles.daysRemainingNormal };
  };

  if (loading) {
    return (
      <ClientShell title="Contracts">
        <div style={styles.container}>
          <p style={styles.loadingText}>Loading contracts...</p>
        </div>
      </ClientShell>
    );
  }

  if (error) {
    return (
      <ClientShell title="Contracts">
        <div style={styles.container}>
          <p style={styles.errorText}>Error: {error}</p>
        </div>
      </ClientShell>
    );
  }

  if (!contracts || contracts.length === 0) {
    return (
      <ClientShell title="Contracts">
        <div style={styles.emptyState}>
          <p>No contracts found</p>
        </div>
      </ClientShell>
    );
  }

  return (
    <ClientShell title="Contracts">
      <div style={styles.container}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Contract Number</th>
              <th style={styles.tableHeaderCell}>Title</th>
              <th style={styles.tableHeaderCell}>Status</th>
              <th style={styles.tableHeaderCell}>Duration</th>
              <th style={styles.tableHeaderCell}>Days Remaining</th>
              <th style={styles.tableHeaderCell}>Auto-Renewal</th>
              <th style={styles.tableHeaderCell}>Value</th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract) => (
              <tr
                key={contract.id}
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f172a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={{ ...styles.tableCell, ...styles.contractNumber }}>
                  {contract.contractNumber}
                </td>
                <td style={{ ...styles.tableCell, ...styles.contractTitle }}>
                  {contract.title}
                </td>
                <td style={styles.tableCell}>
                  {getStatusBadge(contract.status, contract.daysRemaining)}
                </td>
                <td style={{ ...styles.tableCell, ...styles.dates }}>
                  {contract.startDate} to {contract.endDate}
                </td>
                <td style={styles.tableCell}>
                  <div style={getDaysRemainingStyle(contract.daysRemaining)}>
                    {contract.daysRemaining > 0 ? contract.daysRemaining : 'Expired'}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  {contract.autoRenew ? (
                    <span style={styles.renewalIndicator}>Auto-Renew</span>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#9CA3AF' }}>Manual</span>
                  )}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.value}>
                    ${contract.monthlyValue?.toFixed(2) || '0.00'}/mo
                  </div>
                  <div style={{ ...styles.dates, marginTop: '4px' }}>
                    Total: ${contract.totalValue?.toFixed(2) || '0.00'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ClientShell>
  );
}
